import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Page, PageHeader, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtNOK, BILLING } from "@/lib/labels";
import { Plus, Trash2, Package } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Products() {
  const { user } = useAuth();
  const isAdmin = ["admin", "management"].includes(user?.role);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [meta, setMeta] = useState({ areas: [], categories: [], pricing_models: [] });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", code: "", area: "", pricing_model: "fast_maned", price: "", billing_interval: "manedlig", vat_rate: 25, unit: "" });

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/packages").then((r) => setPackages(r.data));
    api.get("/product-meta").then((r) => setMeta(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/products", { ...f, price: Number(f.price) }); toast.success("Produkt opprettet"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/products/${id}`); load(); };

  return (
    <Page>
      <PageHeader title="Produkter og priser" subtitle="Tjenestekatalog, prismodeller og pakker" testid="products-header">
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-product-btn" className="bg-white text-black hover:bg-white/90"><Plus className="w-4 h-4 mr-1" /> Nytt produkt</Button></DialogTrigger>
            <DialogContent className="bg-popover border-border">
              <DialogHeader><DialogTitle>Nytt produkt</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="label-caps">Navn</Label><Input data-testid="product-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
                <div><Label className="label-caps">Kode</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
                <div><Label className="label-caps">Kategori (område)</Label>
                  <Select value={f.area} onValueChange={(v) => setF({ ...f, area: v })}><SelectTrigger className="mt-1 bg-secondary border-border" data-testid="product-area-select"><SelectValue placeholder="Velg område" /></SelectTrigger>
                    <SelectContent className="bg-popover border-border max-h-60">{meta.areas.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="label-caps">Prismodell</Label>
                  <Select value={f.pricing_model} onValueChange={(v) => setF({ ...f, pricing_model: v })}><SelectTrigger className="mt-1 bg-secondary border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border max-h-60">{meta.pricing_models.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="label-caps">Fakturering</Label>
                  <Select value={f.billing_interval} onValueChange={(v) => setF({ ...f, billing_interval: v })}><SelectTrigger className="mt-1 bg-secondary border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">{Object.entries(BILLING).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="label-caps">Pris (NOK)</Label><Input type="number" data-testid="product-price-input" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
                <div><Label className="label-caps">Enhet</Label><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
              </div>
              <DialogFooter><Button data-testid="product-save-btn" onClick={save} className="bg-white text-black hover:bg-white/90">Lagre</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <Tabs defaultValue="produkter">
        <TabsList className="bg-secondary">
          <TabsTrigger value="produkter" data-testid="tab-produkter">Produkter</TabsTrigger>
          <TabsTrigger value="pakker" data-testid="tab-pakker">Pakker</TabsTrigger>
        </TabsList>
        <TabsContent value="produkter">
          <div className="bg-card border border-border rounded-xl overflow-hidden mt-4">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">{["Produkt", "Kode", "Kategori", "Prismodell", "Fakturering", "Pris", ""].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)}</tr></thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-border/50 hover:bg-accent/30">
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.code}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.area || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.pricing_model}</td>
                    <td className="px-4 py-3 text-muted-foreground">{BILLING[p.billing_interval]}</td>
                    <td className="px-4 py-3">{fmtNOK(p.price)}</td>
                    <td className="px-4 py-3">{isAdmin && <button onClick={() => del(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {products.length === 0 && <Empty icon={Package} title="Ingen produkter" />}
          </div>
        </TabsContent>
        <TabsContent value="pakker">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {packages.map((p) => (
              <div key={p.id} className="bg-card border border-border rounded-xl p-5">
                <p className="font-display text-lg">{p.name}</p>
                <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
                <p className="font-display text-2xl text-emerald-400 mt-4">{fmtNOK(p.price)}<span className="text-sm text-muted-foreground">/mnd</span></p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </Page>
  );
}
